import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  ArrowRight,
  MessageCircle,
  ShieldCheck,
  Award,
  Headphones,
  CheckCircle2,
  ThumbsUp,
  Zap,
  Check,
  ChevronRight,
  ChevronLeft,
  Play,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';

export const DEFAULT_HERO_HIGHLIGHTS = [
  {
    id: 'hl-1',
    icon: 'ShieldCheck',
    title: 'Resmi & Terpercaya',
    subtitle: 'Legalitas Lengkap',
    enabled: true,
  },
  {
    id: 'hl-2',
    icon: 'Award',
    title: 'Standar Profesional',
    subtitle: 'Tim Berpengalaman',
    enabled: true,
  },
  {
    id: 'hl-3',
    icon: 'Headphones',
    title: 'Respon Cepat 24/7',
    subtitle: 'Konsultasi Ramah',
    enabled: true,
  },
  {
    id: 'hl-4',
    icon: 'CheckCircle2',
    title: 'Garansi Kualitas',
    subtitle: 'Kepuasan Pelanggan',
    enabled: true,
  },
];

export default function HeroHeadline() {
  const { navigate, settings, categories, setSelectedCategory } = useApp();

  const heroConfig = settings?.heroHeadline;
  const primaryColor = settings?.theme?.primaryColor || '#059669';

  // Touch Swipe for mobile hero slideshow
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);

  const title =
    heroConfig?.staticTitle ||
    settings?.seo?.metaTitle ||
    settings?.siteName ||
    '';
  const subtitle =
    heroConfig?.staticSubtitle ||
    settings?.seo?.metaDescription ||
    settings?.tagline ||
    '';
  const badge = heroConfig?.staticBadge || (settings?.siteName ? 'Verified Hub' : '');
  const category = heroConfig?.staticCategory || (settings?.siteName ? 'Sustainability' : '');
  const coverImage =
    heroConfig?.staticCoverImage ||
    settings?.seo?.ogImage ||
    'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1600&q=80';
  const ctaText = heroConfig?.staticCtaText || 'Explore Solutions';
  const ctaLink = heroConfig?.staticCtaLink || 'products';

  const isExternalUrl = (url?: string) => {
    if (!url) return false;
    const trimmed = url.trim().toLowerCase();
    return (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('//') ||
      trimmed.startsWith('wa.me') ||
      trimmed.startsWith('api.whatsapp.com') ||
      trimmed.startsWith('mailto:') ||
      trimmed.startsWith('tel:')
    );
  };

  const getResolvedUrl = (url?: string) => {
    if (!url) return '#';
    const trimmed = url.trim();
    if (trimmed.startsWith('wa.me') || trimmed.startsWith('api.whatsapp.com')) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    if (!ctaLink) {
      navigate('products');
      return;
    }

    const trimmed = ctaLink.trim();

    if (isExternalUrl(trimmed)) {
      return;
    }

    e.preventDefault();

    let target = trimmed;
    if (target.startsWith('/')) {
      target = target.slice(1);
    }

    const knownRoutes = ['home', 'articles', 'products', 'gallery', 'portfolio', 'admin', 'about', 'contact'];
    if (knownRoutes.includes(target)) {
      navigate(target as any);
    } else if (target.includes('.') && !target.startsWith('/')) {
      window.open(`https://${target}`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('products');
    }
  };

  const mediaType = heroConfig?.mediaType || 'image'; // 'image' | 'gallery' | 'video'
  const galleryImages = (heroConfig?.galleryImages && heroConfig.galleryImages.length > 0)
    ? heroConfig.galleryImages
    : [
        coverImage,
        'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1600&q=80',
        'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1600&q=80',
      ];
  const videoUrl = heroConfig?.videoUrl || 'https://www.youtube.com/watch?v=kJQP7kiw5Fk';
  const isVideoAutoplay = heroConfig?.videoAutoplay !== false;
  const isVideoMuted = heroConfig?.videoMuted !== false;
  const sliderInterval = (heroConfig?.sliderInterval || 5) * 1000;

  // Slideshow & Video state
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Always starts muted for guaranteed iOS/mobile autoplay compatibility
  const [showVideoModal, setShowVideoModal] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Audio Toggle Handler that doesn't freeze the video and respects iOS Safari Autoplay requirements
  const handleToggleAudio = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    const isIOS = typeof navigator !== 'undefined' && (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );

    // On iOS Safari, background iframes are blocked from playing unmuted audio.
    // Opening the Full Video Modal ensures the user gets 100% full sound and controls without browser restriction!
    if (isIOS && isMuted) {
      setShowVideoModal(true);
      return;
    }

    const nextMuted = !isMuted;
    setIsMuted(nextMuted);

    // 1. YouTube Player via postMessage
    if (isYouTube && iframeRef.current?.contentWindow) {
      try {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: nextMuted ? 'mute' : 'unMute',
            args: [],
          }),
          '*'
        );

        if (!nextMuted) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({
              event: 'command',
              func: 'setVolume',
              args: [100],
            }),
            '*'
          );
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({
              event: 'command',
              func: 'playVideo',
              args: [],
            }),
            '*'
          );
        }
      } catch (err) {
        console.warn('YouTube postMessage error:', err);
      }
    }

    // 2. HTML5 Video Element (Direct DOM manipulation with fallback so video never freezes)
    if (videoRef.current) {
      try {
        videoRef.current.muted = nextMuted;
        if (!nextMuted) {
          videoRef.current.volume = 1.0;
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((err) => {
              console.warn('iOS video play catch:', err);
              // If iOS blocks unmuted background playback, silently restore muted so video continues running smoothly!
              if (videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.play().catch(() => {});
              }
              // And open the modal player so the user can hear the sound directly
              setShowVideoModal(true);
            });
          }
        }
      } catch (err) {
        if (videoRef.current) {
          videoRef.current.muted = true;
        }
      }
    }
  };

  // Auto-advance slideshow
  useEffect(() => {
    if (mediaType !== 'gallery' || isPaused || galleryImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % galleryImages.length);
    }, sliderInterval);
    return () => clearInterval(timer);
  }, [mediaType, isPaused, galleryImages.length, sliderInterval]);

  // Extract YouTube ID
  const getYouTubeId = (url: string) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : '';
  };

  const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
  const youtubeId = isYouTube ? getYouTubeId(videoUrl) : '';

  const handleTouchStart = (e: React.TouchEvent) => {
    if (mediaType !== 'gallery' || galleryImages.length <= 1) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (mediaType !== 'gallery' || galleryImages.length <= 1) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        setCurrentSlideIndex((prev) => (prev + 1) % galleryImages.length);
      } else {
        setCurrentSlideIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
      }
    }
  };

  const renderHighlightIcon = (iconName?: string) => {
    switch (iconName) {
      case 'ShieldCheck':
        return <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />;
      case 'Award':
        return <Award className="w-5 h-5 text-amber-400 shrink-0" />;
      case 'Headphones':
        return <Headphones className="w-5 h-5 text-sky-400 shrink-0" />;
      case 'CheckCircle2':
      case 'Check':
        return <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />;
      case 'ThumbsUp':
        return <ThumbsUp className="w-5 h-5 text-blue-400 shrink-0" />;
      case 'Zap':
        return <Zap className="w-5 h-5 text-amber-400 shrink-0" />;
      case 'none':
        return null;
      default:
        return <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />;
    }
  };

  const showHighlights = heroConfig?.showHighlights !== false;
  const rawHighlights =
    heroConfig?.highlights && heroConfig.highlights.length > 0
      ? heroConfig.highlights
      : DEFAULT_HERO_HIGHLIGHTS;
  const activeHighlights = rawHighlights.filter((h) => h.enabled !== false);

  return (
    <section
      id="hero-headline-section"
      className="w-full relative overflow-hidden bg-slate-950 text-white select-none touch-pan-y"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Media Engine */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-slate-950">
        {/* Base Fallback Image (Guarantees immediate high-res visual on mobile while video buffers or if autoplay is restricted by browser) */}
        <img
          src={coverImage}
          alt={title}
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
            mediaType === 'image' ? 'opacity-90 sm:opacity-95' : 'opacity-40'
          }`}
        />

        {mediaType === 'video' ? (
          // 🎥 1 VIDEO BACKGROUND (YouTube / MP4) - Precision full-bleed cover covering 100% viewport width and height
          <div className="w-full h-full absolute inset-0 overflow-hidden pointer-events-none">
            {isYouTube && youtubeId ? (
              <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none flex items-center justify-center">
                <iframe
                  ref={iframeRef}
                  id="hero-youtube-iframe"
                  src={`https://www.youtube.com/embed/${youtubeId}?autoplay=${isVideoAutoplay ? 1 : 0}&mute=1&loop=1&playlist=${youtubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
                  title="Hero Video Background"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320vw] h-[320vh] min-w-[100%] min-h-[100%] sm:w-[177.77vh] sm:h-[56.25vw] object-cover pointer-events-none border-0 opacity-90 sm:opacity-95"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              <video
                ref={videoRef}
                src={videoUrl}
                autoPlay={isVideoAutoplay}
                muted
                loop
                playsInline
                // @ts-ignore
                webkit-playsinline="true"
                preload="auto"
                className="w-full h-full min-w-full min-h-full object-cover opacity-90 sm:opacity-95 absolute inset-0"
              />
            )}
          </div>
        ) : mediaType === 'gallery' ? (
          // 📸 MULTI-FOTO SLIDESHOW BACKGROUND
          <div className="w-full h-full relative">
            {galleryImages.map((imgUrl, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  idx === currentSlideIndex ? 'opacity-90 sm:opacity-95 scale-100' : 'opacity-0 scale-105 pointer-events-none'
                }`}
                style={{ transition: 'opacity 1s ease-in-out, transform 8s ease-out' }}
              >
                <img
                  src={imgUrl}
                  alt={`${title} - slide ${idx + 1}`}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            ))}
          </div>
        ) : (
          // 🖼️ 1 FOTO BACKGROUND TUNGGAL
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-center scale-100 hover:scale-105 transition-transform duration-1000 opacity-90 sm:opacity-95"
          />
        )}

        {/* Soft, Transparent Gradient Overlays for Vivid Photo/Video Visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/50 to-slate-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-slate-950/30" />
      </div>

      {/* Content Container */}
      <div className={`relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-between min-h-[460px] sm:min-h-[560px] md:min-h-[620px] lg:min-h-[680px] ${
        settings?.theme?.transparentHeaderOnHome !== false
          ? 'pt-20 sm:pt-28 md:pt-32 lg:pt-36 pb-8 sm:pb-14 md:pb-16'
          : 'py-6 sm:py-14 md:py-18 lg:py-22'
      }`}>
        <div className="max-w-4xl pt-1 sm:pt-4">
          {/* Badges & Media Indicator */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 mb-3 sm:mb-5">
            <span
              className="px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider text-white shadow-lg flex items-center gap-1.5"
              style={{ backgroundColor: primaryColor }}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
              <span>{badge}</span>
            </span>

            <span
              onClick={() => {
                const matchedCat = categories?.find(
                  (c) => c.name.toLowerCase().trim() === category.toLowerCase().trim() || c.slug.toLowerCase().trim() === category.toLowerCase().trim()
                );
                if (matchedCat) {
                  setSelectedCategory(matchedCat.id);
                }
                navigate('blog');
              }}
              className="px-3.5 py-1 rounded-full text-xs font-bold bg-white/15 hover:bg-white/30 backdrop-blur-md text-white border border-white/20 transition-all cursor-pointer"
              title={`View all articles in category ${category}`}
            >
              {category}
            </span>

            {mediaType === 'video' && (
              <button
                type="button"
                onClick={handleToggleAudio}
                className={`px-3 py-1 rounded-full text-xs font-bold backdrop-blur-md border flex items-center gap-1.5 transition-all cursor-pointer shadow-sm ${
                  !isMuted
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400 ring-2 ring-emerald-400/30'
                    : 'bg-white/15 hover:bg-white/30 text-white border-white/20'
                }`}
                title={isMuted ? 'Unmute Video' : 'Mute Video'}
              >
                {isMuted ? <VolumeX className="w-3.5 h-3.5 text-amber-300" /> : <Volume2 className="w-3.5 h-3.5 text-emerald-300 animate-pulse" />}
                <span className="text-[11px] font-extrabold">{isMuted ? 'Muted (Audio Off)' : 'Audio On 🔊'}</span>
              </button>
            )}

            {mediaType === 'gallery' && galleryImages.length > 1 && (
              <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-white/10 backdrop-blur-md text-slate-300 border border-white/15">
                Foto {currentSlideIndex + 1} dari {galleryImages.length}
              </span>
            )}
          </div>

          {/* Title with Semantic H1 for SEO */}
          <h1
            id="hero-headline-title"
            className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight sm:leading-tight mb-4 sm:mb-6 tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]"
          >
            {title}
          </h1>

          {/* Subtitle */}
          <p className="text-slate-100 text-sm sm:text-base md:text-lg lg:text-xl line-clamp-3 mb-6 sm:mb-8 max-w-3xl leading-relaxed font-normal drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]">
            {subtitle}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-2">
            {isExternalUrl(ctaLink) ? (
              <a
                id="hero-static-cta-btn"
                href={getResolvedUrl(ctaLink)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-4 rounded-xl font-bold text-white bg-white/15 hover:bg-white/25 border border-white/20 backdrop-blur-md flex items-center justify-center gap-2 shadow-xl active:scale-98 transition-all text-xs sm:text-sm tracking-wide cursor-pointer inline-flex"
              >
                <span>{ctaText}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <button
                id="hero-static-cta-btn"
                type="button"
                onClick={handleCtaClick}
                className="px-6 py-4 rounded-xl font-bold text-white bg-white/15 hover:bg-white/25 border border-white/20 backdrop-blur-md flex items-center justify-center gap-2 shadow-xl active:scale-98 transition-all text-xs sm:text-sm tracking-wide cursor-pointer"
              >
                <span>{ctaText}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            {/* Direct Play Video with Full Sound Button (Especially guaranteed for iPhone & mobile devices) */}
            {mediaType === 'video' && (
              <button
                type="button"
                onClick={() => setShowVideoModal(true)}
                className="px-5 py-4 rounded-xl font-bold text-white bg-emerald-600/90 hover:bg-emerald-600 border border-emerald-400/40 backdrop-blur-md flex items-center justify-center gap-2 shadow-xl active:scale-98 transition-all text-xs sm:text-sm tracking-wide cursor-pointer"
                title="Tonton video headline dengan audio penuh dan kontrol lengkap"
              >
                <Play className="w-4 h-4 fill-white text-white" />
                <span>Putar Video dengan Suara 🔊</span>
              </button>
            )}
          </div>
        </div>

        {/* Slideshow Navigation Controls (Only if gallery mode) */}
        {mediaType === 'gallery' && galleryImages.length > 1 && (
          <div className="flex items-center justify-between gap-4 pt-6 mt-4">
            <div className="flex items-center gap-2">
              {galleryImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlideIndex(idx)}
                  className={`h-2.5 rounded-full transition-all cursor-pointer ${
                    idx === currentSlideIndex ? 'w-8 bg-emerald-400' : 'w-2.5 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Pindah ke slide ${idx + 1}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentSlideIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/25 backdrop-blur-md text-white border border-white/20 transition-all cursor-pointer"
                title="Foto Sebelumnya"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentSlideIndex((prev) => (prev + 1) % galleryImages.length)}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/25 backdrop-blur-md text-white border border-white/20 transition-all cursor-pointer"
                title="Foto Selanjutnya"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Integrated Bottom Highlights Grid */}
        {showHighlights && activeHighlights.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-8 sm:mt-10 pt-6 border-t border-white/15">
            {activeHighlights.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 backdrop-blur-xs border border-white/10"
              >
                {renderHighlightIcon(item.icon)}
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">{item.title}</div>
                  <div className="text-[11px] text-slate-400 truncate">{item.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hero Full Video Player Modal (Ensures 100% audio on iPhone & all devices) */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="bg-slate-900 w-full max-w-4xl rounded-3xl shadow-2xl border border-slate-700 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Top Bar */}
            <div className="p-3.5 sm:p-4 bg-slate-950 border-b border-slate-800 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                <span className="text-xs sm:text-sm font-bold truncate max-w-[200px] sm:max-w-md">{title || 'Video Headline Utama'}</span>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Audio Nyala 🔊
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="p-1.5 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white transition-colors cursor-pointer"
                title="Close Video"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Video Screen */}
            <div className="relative aspect-video w-full bg-black flex items-center justify-center">
              {isYouTube && youtubeId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=0&controls=1&playsinline=1&rel=0`}
                  title={title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <video
                  src={videoUrl}
                  autoPlay
                  controls
                  playsInline
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Footer Bar */}
            <div className="p-3.5 bg-slate-950 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
              <span className="text-[11px] sm:text-xs">
                *High quality video playback with full audio controls.
              </span>
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
